import React, {useRef, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const SignatureContainer = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid #d5d8de;
  border-radius: 6px;
  background-color: #f7f7fa;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  cursor: crosshair;
  touch-action: none;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid #929cb9;
  border-radius: 6px;
  color: #2f3d48;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Poppins';

  &:hover {
    border-color: #ff2d38;
    color: #ff2d38;
  }
`;

const SignaturePad = ({onChange, value, width = '100%', height = 200}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');

    // Calculate canvas width based on container
    const updateCanvasSize = () => {
      const containerWidth = container.offsetWidth;
      const canvasHeight = typeof height === 'number' ? height : 200;

      canvas.width = containerWidth;
      canvas.height = canvasHeight;

      // Set canvas styles
      ctx.strokeStyle = '#2f3d48';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    updateCanvasSize();

    // Load existing signature if value exists
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
      };
      img.src = value;
    }
  }, [height, value]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const ctx = canvas.getContext('2d');
      const containerWidth = container.offsetWidth;
      const currentHeight = canvas.height;
      const currentImage = canvas.toDataURL('image/png');

      // Resize canvas
      canvas.width = containerWidth;
      canvas.height = currentHeight;

      // Restore image if exists
      if (currentImage && currentImage !== 'data:,') {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = currentImage;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      if (onChange) {
        onChange(signatureData);
      }
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onChange) {
      onChange('');
    }
  };

  return (
    <SignatureContainer ref={containerRef}>
      <Canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: `${typeof height === 'number' ? height : 200}px`
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {hasSignature && (
        <ClearButton type='button' onClick={clear}>
          Clear
        </ClearButton>
      )}
    </SignatureContainer>
  );
};

SignaturePad.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

SignaturePad.defaultProps = {
  onChange: () => {},
  value: '',
  width: '100%',
  height: 200
};

export default SignaturePad;
